// @flow strict
/* eslint-disable ta-code-conventions/has-matching-test */
import React, { useCallback, useContext } from 'react';
import localStorage from '@ta/common.localstorage';
import { useLocalize } from '@ta/platform.i18n';
import { featureIsEnabled } from '@ta/common.features';
import { Route } from '@ta/lithium.context';
import { type SearchResultsPlaceholderQuery_globalNavLinks_sublinks as QuickLink } from './SearchResultsPlaceholderQuery.gql';
import styles from './QuickLinks.css';

type Props = {|
  geoName?: ?string,
  links: $ReadOnlyArray<?QuickLink>,
  geoId?: ?number,
|};

const isLinkEnabled = (linkId: string) => {
  switch (linkId) {
    case 'scoped-home':
      return false; // This is a navigation bar link only
    case 'cars':
      return featureIsEnabled('rental_cars_winning_quick_link');
    case 'cruises':
      return featureIsEnabled('cruise');
    case 'vr':
      return featureIsEnabled('vacation_rentals');
    case 'vp':
      return featureIsEnabled('seo_vacation_packages_servlet');
    default:
      return true; // The above are special cases, the rest should be enabled
  }
};

/**
 * A replacement for the quick link tiles that appear on /Home. This shows up in the typeahead
 *  overlay if it's open but no query is entered.
 */
const QuickLinks = ({ geoId, geoName, links }: Props) => {
  const [, { uid }] = useContext(Route);
  const { localize } = useLocalize();

  const logQuickLinkClick = useCallback(
    (linkId: string) => {
      // There is no Lithium interactions package at the time this was written, although it's simple
      // enough to do this ourselves.
      const batchedData = localStorage.get('interactionData') || [];

      batchedData.push({
        eventTimestampMsClient: new Date().getTime(),
        interaction: {
          home: {
            quickLinkTypeaheadClick: {
              uid,
              scopedGeoId: geoId,
              trackingKey: linkId,
            },
          },
        },
      });

      localStorage.set('interactionData', batchedData);
    },
    [geoId, uid],
  );

  if (links.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.header}>
        {geoName == null
          ? localize('cx_single_search_explore_tripadvisor') /* EXPLORE TRIPADVISOR */
          : localize('cx_single_search_explore_geo', { geoName }) /* EXPLORE {geoName} */}
      </div>

      <div className={styles.quickLinks}>
        {links.map(link => {
          if (link != null && link.linkId != null && isLinkEnabled(link.linkId)) {
            const { linkId, url, anchorText } = link;
            return (
              <a
                key={linkId}
                className={styles.quickLink}
                href={url}
                onClick={() => logQuickLinkClick(linkId)}
              >
                {anchorText}
              </a>
            );
          }

          return null;
        })}
      </div>
    </>
  );
};

export default QuickLinks;
